import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyS, BrandXXS, LabelS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Dialog } from '@sorare/core/src/components/dialog';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { LeaderboardInlineInfo } from '@sorare/gameplay/src/components/leaderboard/LeaderboardInlineInfo';
import { LeagueDot } from '@sorare/gameplay/src/components/league/LeagueDot';
import { ContenderFooter } from '@sorare/gameplay/src/components/lineup/Contender/ContenderFooter';
import { So5Lineup } from '@sorare/gameplay/src/components/lineup/So5Lineup';

import field from 'components/lineup/LineupLayout/assets/field.webp';

import type {
  FootballSo5LeaderboardContenderDialogQuery,
  FootballSo5LeaderboardContenderDialogQueryVariables,
} from './__generated__/index.graphql';

const SO5_LEADERBOARD_CONTENDER_DIALOG_QUERY = gql`
  query FootballSo5LeaderboardContenderDialogQuery($slug: String!) {
    so5 {
      so5LeaderboardContender(slug: $slug) {
        slug
        so5Leaderboard {
          slug
          stadiumUrl
          division
          divisionChangeConfiguration {
            id
            promotionRankBasisPoint
          }
          so5TournamentType {
            id
            so5LeaderboardType
          }
          so5League {
            slug
            displayName
            ...LeagueDot_so5LeaderboardGroupInterface
          }
          ...LeaderboardInlineInfo_so5Leaderboard
        }
        so5Lineup {
          id
          user {
            slug
            footballUserProfile {
              id
              clubName
            }
            ...UserName_publicUserInfoInterface
            ...Avatar_publicUserInfoInterface
            ...MyClubLink_publicUserInfoInterface
          }
        }
        ...So5Lineup_so5LeaderboardContender
        ...ContenderFooter_so5LeaderboardContender
      }
    }
  }
  ${So5Lineup.fragments.so5LeaderboardContender}
  ${ContenderFooter.fragments.so5LeaderboardContender}
  ${UserName.fragments.user}
  ${Avatar.fragments.publicUserInfoInterface}
  ${MyClubLink.fragments.user}
  ${LeaderboardInlineInfo.fragments.so5Leaderboard}
  ${LeagueDot.fragments.so5LeaderboardGroupInterface}
` as TypedDocumentNode<
  FootballSo5LeaderboardContenderDialogQuery,
  FootballSo5LeaderboardContenderDialogQueryVariables
>;

const Content = styled(Vertical)`
  min-height: 100%;
  justify-content: space-between;
  position: relative;

  &:before {
    content: '';

    position: absolute;
    inset: 0;
    z-index: -1;

    background-image: var(--bg);
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center 25%;
    background-color: var(--c-black);
  }
`;
const Header = styled(SBHorizontal)`
  align-items: start;
  padding: var(--double-unit);
  overflow: hidden;
  > ${Vertical} {
    overflow: hidden;
  }
`;
const LineupWrapper = styled(Vertical)`
  padding: var(--double-unit);
`;

type Props = {
  open: boolean;
  onClose: () => void;
  so5LeaderboardContenderSlug: string;
};

export const So5LeaderboardContenderDialog = ({
  open,
  onClose,
  so5LeaderboardContenderSlug,
}: Props) => {
  const { data, loading } = useQuery(SO5_LEADERBOARD_CONTENDER_DIALOG_QUERY, {
    variables: {
      slug: so5LeaderboardContenderSlug,
    },
  });

  if (loading) {
    return <LoadingIndicator fullHeight />;
  }

  const { so5LeaderboardContender } = data?.so5 || {};
  const { so5Lineup } = so5LeaderboardContender || {};

  if (!so5LeaderboardContender || !so5Lineup) {
    return null;
  }

  const { so5Leaderboard } = so5LeaderboardContender;
  const hasDivisionConfiguration = !!so5Leaderboard.divisionChangeConfiguration;
  const isSpecialWeekly =
    so5Leaderboard.so5TournamentType.so5LeaderboardType ===
    So5LeaderboardType.SPECIAL_WEEKLY;
  return (
    <Dialog open={open} maxWidth="xs" onClose={onClose} hideHeader>
      {({ CloseButton }) => (
        <Content
          style={
            {
              '--bg': `url(${so5Leaderboard.stadiumUrl || field})`,
            } as CSSProperties
          }
        >
          <Header>
            <Vertical>
              <LeaderboardInlineInfo so5Leaderboard={so5Leaderboard}>
                {!!(isSpecialWeekly || hasDivisionConfiguration) && (
                  <>
                    <LeagueDot
                      so5LeaderboardGroupInterface={so5Leaderboard.so5League}
                    />
                    <BrandXXS color="var(--c-nd-600)" bold>
                      {!!isSpecialWeekly &&
                        so5Leaderboard.so5League.displayName}
                      {!!hasDivisionConfiguration && (
                        <FormattedMessage
                          {...fantasy.division}
                          values={{ division: so5Leaderboard.division }}
                        />
                      )}
                    </BrandXXS>
                  </>
                )}
              </LeaderboardInlineInfo>
              <MyClubLink user={so5Lineup.user}>
                <Horizontal gap={1.5}>
                  <Avatar user={so5Lineup.user} rounded size={2} />
                  <Vertical gap={0}>
                    <BodyS bold>
                      <Ellipsis>
                        {so5Lineup.user.footballUserProfile?.clubName}
                      </Ellipsis>
                    </BodyS>
                    <LabelS color="var(--c-nd-600)">
                      <Ellipsis>
                        <UserName user={so5Lineup.user} />
                      </Ellipsis>
                    </LabelS>
                  </Vertical>
                </Horizontal>
              </MyClubLink>
            </Vertical>
            <CloseButton onClose={onClose} />
          </Header>
          <LineupWrapper>
            <So5Lineup so5LeaderboardContender={so5LeaderboardContender} />
            <ContenderFooter
              so5LeaderboardContender={so5LeaderboardContender}
            />
          </LineupWrapper>
        </Content>
      )}
    </Dialog>
  );
};
